import { useEffect, useState } from 'react'

import { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '@/platform/family/api'
import {
  type ConsentChange,
  type ConsentStatus,
  type MaterialConsentApi,
} from '@/platform/family/consent'
import { toAppError } from '@/shared/errors/envelope'
import { PinPad } from '@/shared/ui/PinPad'

import { consentNotice } from './consent-notice'

/**
 * Parent consent to school-material processing (NFR-05, D-15), in Parent
 * Home → Gia đình. The parent reads the notice for the version the server
 * names, ticks an unticked box, and confirms with the parent PIN; the server
 * checks role, version and PIN and records it (migration 0010). Withdrawal
 * also needs the PIN. Nothing here grants anything by itself.
 */

export interface MaterialConsentCardProps {
  readonly api: MaterialConsentApi
}

type Step = 'summary' | 'notice' | 'grant_pin' | 'withdraw_pin'

function day(iso: string | null): string {
  return iso === null ? '' : new Date(iso).toLocaleDateString('vi-VN')
}

function statusLine(s: ConsentStatus): string {
  switch (s.status) {
    case 'granted':
      return `Đã đồng ý ngày ${day(s.granted_at)} (thông báo ${s.policy_version}).`
    case 'outdated':
      return 'Thông báo đã thay đổi. Bố mẹ cần đọc và đồng ý lại; trong lúc chờ, ứng dụng không gửi tài liệu hay giọng đọc.'
    case 'withdrawn':
      return `Đã rút lại đồng ý ngày ${day(s.withdrawn_at)}. Ứng dụng không gửi tài liệu hay giọng đọc.`
    case 'none':
      return 'Chưa đồng ý. Ứng dụng không gửi tài liệu hay giọng đọc nào cho tới khi bố mẹ đồng ý.'
  }
}

export function MaterialConsentCard({
  api,
}: MaterialConsentCardProps): React.JSX.Element {
  const [status, setStatus] = useState<ConsentStatus | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [step, setStep] = useState<Step>('summary')
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const load = async () => {
    setStatus(await api.status())
  }

  useEffect(() => {
    let active = true
    api.status().then(
      (s) => {
        if (active) setStatus(s)
      },
      () => {
        if (active) setLoadFailed(true)
      },
    )
    return () => {
      active = false
    }
  }, [api])

  const reset = () => {
    setStep('summary')
    setAgreed(false)
    setError(null)
  }

  const apply = async (call: () => Promise<ConsentChange>, done: string) => {
    setBusy(true)
    setError(null)
    try {
      const r = await call()
      if (r.status === 'pin_rejected') {
        setError(
          r.locked_until !== null
            ? 'Đã nhập sai PIN 5 lần. Vui lòng thử lại sau 5 phút.'
            : `PIN chưa đúng. Còn ${String(r.attempts_remaining)} lần thử.`,
        )
        return
      }
      await load()
      reset()
      setNote(
        r.status === 'policy_version_mismatch'
          ? 'Thông báo vừa được cập nhật. Vui lòng đọc lại trước khi đồng ý.'
          : done,
      )
    } catch (e) {
      setError(
        toAppError(e).code === 'SESSION_EXPIRED'
          ? 'Phiên đăng nhập đã hết hạn.'
          : 'Không lưu được. Vui lòng thử lại.',
      )
    } finally {
      setBusy(false)
    }
  }

  const notice = status === null ? null : consentNotice(status.policy_version)

  return (
    <section
      aria-labelledby="material-consent"
      className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-800"
    >
      <h2 id="material-consent" className="text-lg font-semibold">
        Gửi tài liệu và giọng đọc
      </h2>

      {status === null ? (
        <p className="text-slate-600 dark:text-slate-400">
          {loadFailed ? 'Không tải được trạng thái đồng ý.' : 'Đang tải…'}
        </p>
      ) : step === 'summary' ? (
        <>
          <p className="text-slate-700 dark:text-slate-300">{statusLine(status)}</p>
          {note !== null && (
            <p role="status" className="text-emerald-800 dark:text-emerald-300">
              {note}
            </p>
          )}
          {!status.can_manage ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Chỉ phụ huynh quản lý gia đình mới thay đổi được mục này.
            </p>
          ) : notice === null ? (
            <p role="alert" className="text-amber-900 dark:text-amber-200">
              Ứng dụng cần được cập nhật để hiển thị thông báo mới nhất trước khi bố mẹ
              đồng ý.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {status.status !== 'granted' && (
                <button
                  type="button"
                  className="h-11 rounded-xl bg-sky-700 px-4 font-semibold text-white"
                  onClick={() => {
                    setNote(null)
                    setStep('notice')
                  }}
                >
                  Đọc thông báo và đồng ý
                </button>
              )}
              {status.status === 'granted' && (
                <>
                  <button
                    type="button"
                    className="h-11 rounded-xl border border-slate-300 px-4 dark:border-slate-700"
                    onClick={() => {
                      setNote(null)
                      setStep('notice')
                    }}
                  >
                    Xem thông báo
                  </button>
                  <button
                    type="button"
                    className="h-11 rounded-xl border border-rose-300 px-4 text-rose-800 dark:border-rose-800 dark:text-rose-300"
                    onClick={() => {
                      setNote(null)
                      setStep('withdraw_pin')
                    }}
                  >
                    Rút lại đồng ý
                  </button>
                </>
              )}
            </div>
          )}
        </>
      ) : step === 'notice' && notice !== null ? (
        <>
          <article className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto rounded-xl bg-slate-50 p-4 dark:bg-slate-900">
            <h3 className="font-semibold">{notice.title}</h3>
            {notice.sections.map((s) => (
              <div key={s.heading}>
                <h4 className="font-medium">{s.heading}</h4>
                {s.paragraphs.map((p) => (
                  <p key={p} className="mt-1 text-slate-700 dark:text-slate-300">
                    {p}
                  </p>
                ))}
              </div>
            ))}
            <p className="text-sm text-slate-500">
              Phiên bản thông báo: {notice.version}
            </p>
          </article>
          {status.status === 'granted' ? (
            <button
              type="button"
              className="h-11 self-start rounded-xl border border-slate-300 px-4 dark:border-slate-700"
              onClick={reset}
            >
              Đóng
            </button>
          ) : (
            <>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 size-5"
                  checked={agreed}
                  onChange={(e) => {
                    setAgreed(e.target.checked)
                  }}
                />
                <span>
                  Tôi là phụ huynh của gia đình này, đã đọc thông báo trên và đồng ý cho
                  ứng dụng gửi tài liệu và giọng đọc của con tới các dịch vụ nêu trong
                  thông báo.
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!agreed}
                  className="h-11 rounded-xl bg-sky-700 px-4 font-semibold text-white disabled:opacity-50"
                  onClick={() => {
                    setStep('grant_pin')
                  }}
                >
                  Đồng ý
                </button>
                <button
                  type="button"
                  className="h-11 rounded-xl border border-slate-300 px-4 dark:border-slate-700"
                  onClick={reset}
                >
                  Không đồng ý
                </button>
              </div>
            </>
          )}
        </>
      ) : step === 'grant_pin' && notice !== null ? (
        <PinPad
          title="Nhập PIN phụ huynh để xác nhận đồng ý"
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          busy={busy}
          submitLabel="Xác nhận đồng ý"
          onSubmit={(pin) => {
            void apply(() => api.grant(notice.version, pin), 'Đã ghi nhận đồng ý.')
          }}
          onCancel={reset}
        />
      ) : (
        <PinPad
          title="Nhập PIN phụ huynh để rút lại đồng ý"
          hint="Sau khi rút lại, ứng dụng không gửi thêm tài liệu hay giọng đọc nào."
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          busy={busy}
          submitLabel="Rút lại đồng ý"
          onSubmit={(pin) => {
            void apply(() => api.withdraw(pin), 'Đã rút lại đồng ý.')
          }}
          onCancel={reset}
        />
      )}
    </section>
  )
}
